import type { KeyboardEvent } from "react";
import { copy } from "../copy";

export type ContestTab = "details" | "join" | "vote" | "activity" | "reviews";

type Props = {
  tabs: ContestTab[];
  active: ContestTab;
  onChange: (tab: ContestTab) => void;
  counts?: Partial<Record<ContestTab, number>>;
};

// Roving tabs: arrow keys move between them and only the active tab is in the
// tab order. On a phone the row scrolls sideways instead of wrapping.
export default function ContestTabs({ tabs, active, onChange, counts = {} }: Props) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.indexOf(active);
    if (event.key === "ArrowRight") {
      event.preventDefault();
      onChange(tabs[(i + 1) % tabs.length]);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      onChange(tabs[(i - 1 + tabs.length) % tabs.length]);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={copy.contest.tablist}
      onKeyDown={onKeyDown}
      className="bf-no-scrollbar -mx-4 flex gap-1 overflow-x-auto border-b border-ground-edge px-4 md:mx-0 md:px-0"
    >
      {tabs.map((tab) => {
        const isActive = tab === active;
        const count = counts[tab];
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            id={`tab-${tab}`}
            aria-selected={isActive}
            aria-controls={`panel-${tab}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab)}
            className={`bf-focusable relative shrink-0 cursor-pointer whitespace-nowrap px-3.5 py-3.5 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
              isActive ? "text-chrome" : "text-chrome-dim hover:text-chrome"
            }`}
          >
            {copy.contest.tabs[tab]}
            {typeof count === "number" && count > 0 && <span className="bf-tabular ml-1.5 text-[10px] text-gold">{count}</span>}
            {isActive && <span aria-hidden className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-gold" />}
          </button>
        );
      })}
    </div>
  );
}
